// usePayLogic.ts
import { useContext, useEffect, useMemo, useState } from "react"
import { PriceContext } from "../PriceContext"
import addonDropdownPricing from "../addonDropdownPricing.json"

interface UsePayLogicProps {
  price: number
  bucket: string // core / support / marketing / ...
  title: string // отображаемое имя аддона — используется в ключах состояния
  addonKey: string // стабильный идентификатор аддона: "aiBot" | "messengers" | "email" | ...
}

export const usePayLogic = ({ price, bucket, title, addonKey }: UsePayLogicProps) => {
  const {
    calculator,
    range,
    addons,
    setAddons,
    addonsCount,
    setAddonsCount,
    addonsList,
    setAddonsList,
    addonsSelect,
    setAddonsSelect,
    text,
    addonsPicked,
    setAddonsPicked,
  } = useContext(PriceContext)

  // Тип аддона определяется по стабильному ключу, а не по переводимому заголовку
  const isAIBot = addonKey === "aiBot"
  const isEmail = addonKey === "email"
  const isMessengers = addonKey === "messengers"
  const isDropdown = isAIBot || isEmail || isMessengers

  // Таблицы опций/цен дропдаунов — единый источник в addonDropdownPricing.json
  // (одинаковы для обеих поверхностей; раньше были захардкожены здесь)
  const aiBotOptions = addonDropdownPricing.aiBot.options
  const aiBotPricing = addonDropdownPricing.aiBot.pricing

  const emailOptions = addonDropdownPricing.email.options
  const emailPricing = addonDropdownPricing.email.pricing

  const messengersOptions = addonDropdownPricing.messengers.options
  const messengersPricing = addonDropdownPricing.messengers.pricing

  const fieldKey = `${bucket}__${title}` // для addonsSelect
  const fieldCheckedKey = `${title}__${bucket}` // для addonsList

  const [localIndex, setLocalIndex] = useState<number>(addonsSelect?.[fieldKey] ?? 0)

  useEffect(() => {
    if (addonsSelect && Object.prototype.hasOwnProperty.call(addonsSelect, fieldKey)) {
      setLocalIndex(addonsSelect[fieldKey])
    } else {
      setLocalIndex(0)
    }
  }, [addonsSelect, fieldKey])

  const [id] = useState(() => `addon-${bucket}-${title}`.replace(/\s+/g, "-"))

  const selectedPrice = useMemo(() => {
    if (isAIBot) return aiBotPricing[localIndex] ?? aiBotPricing[0]
    if (isEmail) return emailPricing[localIndex] ?? emailPricing[0]
    if (isMessengers) return messengersPricing[localIndex] ?? messengersPricing[0]
    return price
  }, [isAIBot, isEmail, isMessengers, localIndex, price])

  const selectedCount = useMemo(() => {
    if (isAIBot) return aiBotOptions[localIndex] ?? aiBotOptions[0]
    if (isEmail) return emailOptions[localIndex] ?? emailOptions[0]
    if (isMessengers) return messengersOptions[localIndex] ?? messengersOptions[0]
    return null
  }, [isAIBot, isEmail, isMessengers, localIndex])

  const checked = useMemo(() => {
    return addonsList?.includes(fieldCheckedKey) ?? false
  }, [addonsList, fieldCheckedKey])

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isOn = e.target.checked

    const addonKey = fieldCheckedKey // `${title}__${bucket}`

    if (isOn) {
      // Сохраняем реальную цену включения (истина)
      setAddonsPicked((prev) => ({
        ...(prev ?? {}),
        [addonKey]: {
          bucket,
          price: selectedPrice,
          index: isDropdown ? localIndex : undefined,
        },
      }))

      // Сумма по бакету + выбранная цена
      setAddons((prev) => {
        const prevVal = prev?.[bucket] ?? 0
        return { ...(prev ?? {}), [bucket]: prevVal + selectedPrice }
      })

      // Счетчик — увеличиваем только если реально не был выбран
      setAddonsCount((prev) => {
        const already = (addonsList ?? []).includes(addonKey)
        if (already) return prev ?? {}
        return { ...(prev ?? {}), [bucket]: (prev?.[bucket] ?? 0) + 1 }
      })

      // Список выбранных — без дублей
      setAddonsList((prev) => {
        const list = [...(prev ?? [])]
        if (!list.includes(addonKey)) list.push(addonKey)
        return list
      })

      // Для дропдауна сохраняем индекс
      if (isDropdown) {
        setAddonsSelect((prev) => ({
          ...(prev ?? {}),
          [fieldKey]: localIndex,
        }))
      }

      return
    }

    // Выключаем: вычитаем ту цену, которую реально добавляли
    const removePrice = addonsPicked?.[addonKey]?.price ?? 0

    setAddons((prev) => {
      const prevVal = prev?.[bucket] ?? 0
      return { ...(prev ?? {}), [bucket]: prevVal - removePrice }
    })

    setAddonsCount((prev) => {
      const already = (addonsList ?? []).includes(addonKey)
      if (!already) return prev ?? {}
      return { ...(prev ?? {}), [bucket]: (prev?.[bucket] ?? 0) - 1 }
    })

    setAddonsList((prev) => {
      const list = [...(prev ?? [])]
      const idx = list.indexOf(addonKey)
      if (idx !== -1) list.splice(idx, 1)
      return list
    })

    setAddonsPicked((prev) => {
      const next = { ...(prev ?? {}) }
      delete next[addonKey]
      return next
    })
  }

  const handleSelect = (newIndex: number) => {
    if (!isDropdown) return

    const oldIndex = localIndex
    if (newIndex === oldIndex) {
      setLocalIndex(newIndex)
      return
    }

    setLocalIndex(newIndex)

    setAddonsSelect((prev) => ({
      ...(prev ?? {}),
      [fieldKey]: newIndex,
    }))

    if (!checked) return

    const prices = isAIBot ? aiBotPricing : isMessengers ? messengersPricing : emailPricing

    const oldPrice = prices[oldIndex] ?? prices[0] ?? 0
    const newPrice = prices[newIndex] ?? prices[0] ?? 0
    const diff = newPrice - oldPrice

    setAddons((prev) => ({
      ...(prev ?? {}),
      [bucket]: (prev?.[bucket] ?? 0) + diff,
    }))

    // Обновляем сохранённую цену, чтобы выключение вычло правильно
    const addonKey = fieldCheckedKey
    setAddonsPicked((prev) => ({
      ...(prev ?? {}),
      [addonKey]: {
        bucket,
        price: newPrice,
        index: newIndex,
      },
    }))
  }

  return {
    id,
    text,
    checked,
    isAIBot,
    isEmail,
    isMessengers,
    isDropdown,
    selectedPrice,
    selectedCount,
    localIndex,
    aiBotOptions,
    aiBotPricing,
    emailOptions,
    emailPricing,
    messengersOptions,
    messengersPricing,
    handleToggle,
    handleSelect,
  }
}
